import React, { useState, useEffect } from 'react';
import {
  AccountIcon,
  TruckIcon,
  CloseIcon,
  PrinterIcon,
  EmailIcon,
  PhoneIcon,
  ProductCatalogIcon,
  FileIcon,
  CalendarIcon,
  CartIcon
} from '../../../public/icons';
import adminOrderService from '../../../services/admin/admin-orderService';

/* ───────── Helpers ───────── */
const formatCurrency = (n) => `₦${Number(n || 0).toLocaleString()}`;

const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';

const humanStatus = (s) =>
  (s || '')
    .split('_')
    .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
    .join(' ');

/* ───────── Tag chip ───────── */
const Chip = ({ children, color = 'gray' }) => {
  const palette = {
    gray: 'bg-gray-100 text-gray-700',
    green: 'bg-[#E7F4ED] text-[#2F8F5B]',
    red: 'bg-[#F4E7E7] text-[#A82121]',
    yellow: 'bg-[#FFF4D6] text-[#D8A100]',
    blue: 'bg-[#EBEFFF] text-[#194F9B]',
    purple: 'bg-[#F2E7F4] text-[#6F1197]'
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${palette[color]}`}
    >
      {children}
    </span>
  );
};

/* ───────── Detail Row ───────── */
const DetailRow = ({ label, value }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex justify-between gap-4 text-xs">
      <span className="text-[#8A9099]">{label}</span>
      <span className="text-[#0E1628] text-right break-words max-w-[65%]">
        {value}
      </span>
    </div>
  );
};

const OrderDetailsModal = ({ isOpen, onClose, order }) => {
  const [internalNote, setInternalNote] = useState('');
  const [status, setStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Sync local state when the incoming order changes
  useEffect(() => {
    if (order) {
      setInternalNote(order.adminNotes || '');
      setStatus(order.orderStatus || 'pending');
      setTrackingNumber(order.trackingNumber || '');
    }
  }, [order]);

  if (!isOpen || !order) return null;

  /* ───────── Derived values ───────── */
  const placed = formatDateTime(order.createdAt);

  const statusColor = (s) => {
    const map = {
      pending: 'bg-[#EBEFFF] text-[#194F9B] border-[#A6C5FA]',
      processing: 'bg-[#FFF4D6] text-[#D8A100] border-[#FFE08A]',
      content_review: 'bg-[#EBEFFF] text-[#194F9B] border-[#A6C5FA]',
      in_production: 'bg-[#FFF4D6] text-[#D8A100] border-[#FFE08A]',
      shipped: 'bg-[#F2E7F4] text-[#6F1197] border-[#D9B9E6]',
      delivered: 'bg-[#E7F4ED] text-[#2F8F5B] border-[#B7E0C7]',
      cancelled: 'bg-[#F4E7E7] text-[#A82121] border-[#E6B9B9]'
    };
    return map[s] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  /* ───────── Timeline ───────── */
  const buildTimeline = () => {
    const placedDone = !!order.createdAt;
    const paidDone = order.paymentStatus === 'paid';
    const processingDone = [
      'processing',
      'content_review',
      'in_production',
      'shipped',
      'delivered'
    ].includes(order.orderStatus);
    const shippedDone = ['shipped', 'delivered'].includes(order.orderStatus);
    const deliveredDone = order.orderStatus === 'delivered';

    return [
      {
        event: 'Order Placed',
        date: placed,
        completed: placedDone
      },
      {
        event: 'Payment Confirmed',
        date: order.paidAt ? formatDateTime(order.paidAt) : 'Pending',
        completed: paidDone
      },
      {
        event: 'In Production',
        date: processingDone ? 'Started' : 'Pending',
        completed: processingDone
      },
      {
        event: 'Shipped',
        date: shippedDone ? 'Shipped' : 'Pending',
        completed: shippedDone
      },
      {
        event: 'Delivered',
        date: deliveredDone ? 'Delivered' : 'Pending',
        completed: deliveredDone
      }
    ];
  };

  const timelineItems = buildTimeline();

  /* ───────── Save changes ───────── */
  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await adminOrderService.updateOrderStatus(order._id, {
        orderStatus: status,
        trackingNumber,
        adminNotes: internalNote
      });
      if (res.success) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      }
    } catch (err) {
      console.error('Save order error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[500]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[999] flex items-start justify-end">
        <div className="bg-white w-full max-w-4xl h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 text-left border-b border-gray-200">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Order #{order.orderNumber}
              </h1>
              <p className="text-sm font-light text-[#6B635D] mt-1">
                Placed on {placed}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Status Section */}
          <div
            className={`p-4 mx-6 rounded-sm mt-4 border ${statusColor(
              order.orderStatus
            )}`}
          >
            <div className="flex items-center justify-between text-left">
              <div>
                <p className="text-sm font-normal text-[#8A9099] mb-1">
                  Current Status
                </p>
                <p className="text-base font-medium">
                  {humanStatus(order.orderStatus)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="px-3 py-2 bg-white border border-gray-200 text-sm text-[#0E1628] focus:outline-none"
                >
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="content_review">Content Review</option>
                  <option value="in_production">In Production</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ───────── LEFT COLUMN ───────── */}
              <div className="space-y-6">
                {/* Customer */}
                <div className="bg-[#F9FAFB] p-5 text-left">
                  <div className="flex items-center gap-2 mb-4">
                    <AccountIcon className="w-6 h-6 text-[#8A9099]" />
                    <h2 className="text-lg font-medium text-[#0E1628]">
                      Customer Information
                    </h2>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-[#8A9099]">Name</p>
                      <p className="font-normal text-[#0E1628]">
                        {order.customer?.firstName} {order.customer?.lastName}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <EmailIcon color="#1E1E1E" />
                      <p className="font-normal text-[#0E1628] break-all">
                        {order.customer?.email}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <PhoneIcon color="#0E1628" size="24" />
                      <p className="text-gray-900">
                        {order.customer?.phone || '—'}
                      </p>
                    </div>
                    {order.user && (
                      <div>
                        <p className="text-sm text-[#8A9099] mb-1">
                          Linked Account
                        </p>
                        <p className="text-[#0E1628] text-sm">
                          {order.user?.firstName} {order.user?.lastName} ·{' '}
                          {order.user?.email}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Delivery */}
                <div className="bg-[#F9FAFB] p-5 text-left">
                  <div className="flex items-center gap-2 mb-4">
                    <TruckIcon className="w-5 h-5 text-[#55000C]" />
                    <h2 className="text-lg font-medium text-[#0E1628]">
                      Delivery
                    </h2>
                  </div>
                  <div className="space-y-1.5">
                    <DetailRow label="Address" value={order.delivery?.address} />
                    <DetailRow label="City" value={order.delivery?.city} />
                    <DetailRow label="State" value={order.delivery?.state} />
                    <DetailRow label="Country" value={order.delivery?.country} />
                    <DetailRow
                      label="Delivery Note"
                      value={order.delivery?.deliveryNote}
                    />
                  </div>
                </div>

                {/* Internal Note */}
                <div className="bg-[#F9FAFB] p-5 text-left">
                  <div className="flex items-center gap-2 mb-4">
                    <FileIcon className="w-5 h-5 text-[#8A9099]" />
                    <h2 className="text-lg font-semibold text-gray-900">
                      Internal Note
                    </h2>
                  </div>
                  <textarea
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    className="w-full p-3 border border-[#DBDBDB] bg-transparent font-light resize-none h-24 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Add internal notes..."
                  />
                </div>

                {/* Fulfillment */}
                <div className="bg-[#F9FAFB] p-5 text-left">
                  <div className="flex items-center gap-2 mb-4">
                    <TruckIcon className="w-5 h-5 text-[#55000C]" />
                    <h2 className="text-lg font-medium text-[#0E1628]">
                      Fulfillment
                    </h2>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-normal text-[#8A9099] mb-1">
                        Tracking Number
                      </label>
                      <input
                        type="text"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="Add tracking number..."
                        className="w-full p-2 border border-[#DBDBDB] bg-white text-sm text-[#0E1628] focus:outline-none focus:ring-1 focus:ring-[#D2A146]"
                      />
                    </div>
                    <DetailRow
                      label="Payment Status"
                      value={humanStatus(order.paymentStatus)}
                    />
                  </div>
                </div>
              </div>

              {/* ───────── RIGHT COLUMN ───────── */}
              <div className="space-y-6">
                {/* Order Timeline */}
                <div className="bg-[#F9FAFB] p-5 text-left">
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarIcon className="w-5 h-5 text-[#8A9099]" />
                    <h2 className="text-lg font-medium text-[#0E1628]">
                      Order Timeline
                    </h2>
                  </div>
                  <div className="space-y-4">
                    {timelineItems.map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex flex-col items-center">
                          <div
                            className={`w-3 h-3 rounded-full ${
                              item.completed ? 'bg-[#55000C]' : 'bg-gray-400'
                            }`}
                          />
                          {index < timelineItems.length - 1 && (
                            <div
                              className={`w-0.5 h-10 ${
                                item.completed ? 'bg-[#55000C]' : 'bg-gray-300'
                              }`}
                            />
                          )}
                        </div>
                        <div>
                          <p
                            className={`font-normal text-base ${
                              item.completed
                                ? 'text-gray-900'
                                : 'text-gray-500'
                            }`}
                          >
                            {item.event}
                          </p>
                          <p className="text-sm font-light text-[#6B635D]">
                            {item.date}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="bg-[#F9FAFB] p-5 text-left">
                  <div className="flex items-center gap-2 mb-4">
                    <CartIcon className="w-5 h-5 text-[#8A9099]" />
                    <h2 className="text-lg font-medium text-[#0E1628]">
                      Payment
                    </h2>
                  </div>
                  <div className="space-y-1.5">
                    <DetailRow
                      label="Method"
                      value={order.paymentMethod || 'Paystack'}
                    />
                    <DetailRow
                      label="Reference"
                      value={order.paymentReference}
                    />
                    <DetailRow
                      label="Paid At"
                      value={order.paidAt ? formatDateTime(order.paidAt) : '—'}
                    />
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-xs text-[#8A9099]">Status</span>
                      <Chip
                        color={
                          order.paymentStatus === 'paid'
                            ? 'green'
                            : order.paymentStatus === 'failed'
                            ? 'red'
                            : 'yellow'
                        }
                      >
                        {order.paymentStatus === 'paid'
                          ? 'Payment Confirmed'
                          : order.paymentStatus === 'failed'
                          ? 'Payment Failed'
                          : 'Payment Pending'}
                      </Chip>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ───────── FULL-WIDTH ITEMS SECTION ───────── */}
            <div className="mt-6">
              <div className="bg-[#F9FAFB] p-5 text-left">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ProductCatalogIcon className="w-5 h-5 text-[#8A9099]" />
                    <h2 className="text-lg font-semibold text-gray-900">
                      Items ({order.items?.length || 0})
                    </h2>
                  </div>
                </div>

                <div className="space-y-6">
                  {order.items?.map((item, idx) => (
                    <OrderItemDetail key={idx} item={item} index={idx} />
                  ))}
                </div>

                {/* Totals */}
                <div className="space-y-2 mt-6 pt-4 border-t border-[#E8E8E8]">
                  <div className="flex justify-between">
                    <span className="text-[#8A9099] text-xs">Subtotal</span>
                    <span className="text-[#0E1628] text-xs">
                      {formatCurrency(order.subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8A9099] text-xs">
                      Memory Included
                    </span>
                    <span className="text-[#0E1628] text-xs">
                      {formatCurrency(order.memoryIncluded)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8A9099] text-xs">Shipping</span>
                    <span className="text-[#0E1628] text-xs">
                      {formatCurrency(order.shippingFee)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-[#E8E8E8]">
                    <span className="text-[#0E1628] text-sm font-medium">
                      Total
                    </span>
                    <span className="text-[#0E1628] text-sm font-semibold">
                      {formatCurrency(order.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white p-6 border-t border-gray-200">
            <div className="flex gap-3">
              <button
                onClick={handlePrint}
                className="flex max-w-40 items-center justify-center gap-2 py-3 border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 flex-1"
              >
                <PrinterIcon className="w-5 h-5" />
                Print Invoice
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`w-2/3 py-3 text-white font-medium flex-1 transition-colors ${
                  saving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : saved
                    ? 'bg-[#2F8F5B]'
                    : 'bg-primary hover:bg-yellow-700'
                }`}
              >
                {saving ? 'Saving…' : saved ? '✅ Saved' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Order Item Detail — shows EVERYTHING for one line item
═══════════════════════════════════════════════════════════════ */
const OrderItemDetail = ({ item, index }) => {
  const c = item.customization || {};
  const magnets = c.magnets || item.magnets || [];

  return (
    <div className="border border-[#E8E8E8] bg-white p-4">
      {/* Item header */}
      <div className="flex gap-3 pb-4 border-b border-[#F0F0F0]">
        <div className="w-20 h-20 flex-shrink-0 overflow-hidden bg-[#F5F5F5]">
          {item.image && (
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-medium text-[#1C2A46] text-sm">
              {item.title}
            </h3>
            <span className="text-sm font-semibold text-[#0E1628] whitespace-nowrap">
              {formatCurrency(item.lineTotal || item.unitPrice * item.quantity)}
            </span>
          </div>
          {item.description && (
            <p className="text-xs italic text-[#6B635D] mt-0.5">
              {item.description}
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            {item.isCustom && <Chip color="blue">Custom</Chip>}
            {item.slug && <Chip>{item.slug}</Chip>}
            <Chip>Qty: {item.quantity}</Chip>
            {item.unitPrice && (
              <Chip>Unit: {formatCurrency(item.unitPrice)}</Chip>
            )}
          </div>
        </div>
      </div>

      {/* Item details grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 py-4 border-b border-[#F0F0F0]">
        <DetailRow label="Product ID" value={item.productId} />
        <DetailRow label="Slug" value={item.slug} />
        <DetailRow label="Magnet ID" value={item.magnetId} />
        <DetailRow label="Magnet Name" value={item.magnetName} />
        <DetailRow label="Collection" value={item.collectionName} />
        <DetailRow label="Gift Item" value={item.giftItemLabel} />
        <DetailRow label="Gift Item (code)" value={item.giftItem} />
        <DetailRow label="Message Title" value={item.messageTitle} />
        <DetailRow
          label="Reveal Date"
          value={item.revealDate ? formatDateTime(item.revealDate) : null}
        />
        <DetailRow label="Audio Source" value={item.audioSource} />
        <DetailRow
          label="Personalised Message"
          value={item.personalizedMessage}
        />
      </div>

      {/* Recipient */}
      {item.recipient &&
        (item.recipient.name ||
          item.recipient.email ||
          item.recipient.phone) && (
          <div className="py-4 border-b border-[#F0F0F0]">
            <p className="text-xs font-medium text-[#0E1628] mb-2">
              Recipient
            </p>
            <div className="space-y-1.5">
              <DetailRow label="Name" value={item.recipient.name} />
              <DetailRow label="Email" value={item.recipient.email} />
              <DetailRow label="Phone" value={item.recipient.phone} />
            </div>
          </div>
        )}

      {/* Audio */}
      {(item.audioFile?.url || item.audioUrl) && (
        <div className="py-4 border-b border-[#F0F0F0]">
          <p className="text-xs font-medium text-[#0E1628] mb-2">Audio</p>
          {item.audioFile?.url ? (
            <div className="space-y-2">
              <audio
                src={item.audioFile.url}
                controls
                className="w-full max-w-sm"
              />
              {item.audioFile.name && (
                <p className="text-xs text-[#8A9099]">
                  File: {item.audioFile.name}
                </p>
              )}
            </div>
          ) : (
            <a
              href={item.audioUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#D2A146] underline break-all"
            >
              {item.audioUrl}
            </a>
          )}
        </div>
      )}

      {/* Customization (raw values) */}
      {Object.keys(c).length > 0 && (
        <div className="py-4 border-b border-[#F0F0F0]">
          <p className="text-xs font-medium text-[#0E1628] mb-2">
            Customization
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            <DetailRow label="Set Size" value={c.setSize} />
            <DetailRow label="Colour" value={c.color} />
            <DetailRow label="Magnet Type" value={c.magnetType} />
            <DetailRow label="Magnet Set Count" value={c.magnetSetCount} />
            <DetailRow label="Frame Count" value={c.frameCount} />
            <DetailRow label="Personal Note" value={c.personalNote} />
            {c.extras?.selectedExtras?.length > 0 && (
              <DetailRow
                label="Extras"
                value={c.extras.selectedExtras.join(', ')}
              />
            )}
            <DetailRow
              label="Audio Gift Item"
              value={c.extras?.selectedAudioItem}
            />
            <DetailRow
              label="Audio Content"
              value={c.extras?.audioContent}
            />
            <DetailRow
              label="Audio Gift Source"
              value={c.audioSource}
            />
          </div>

          {/* Future gifts */}
          {c.futureGift?.futureGifts?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-[#0E1628] mb-2">
                Future Gifts ({c.futureGift.futureGifts.length})
              </p>
              <div className="space-y-2">
                {c.futureGift.futureGifts.map((fg, i) => (
                  <div
                    key={i}
                    className="bg-[#F5F5F5] border border-[#E8E8E8] p-2 text-xs"
                  >
                    <DetailRow
                      label="Title"
                      value={fg.title || `Gift ${i + 1}`}
                    />
                    <DetailRow label="Delivery Date" value={fg.deliveryDate} />
                    <DetailRow label="Gift Type" value={fg.giftType} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Magnets gallery */}
      {magnets.length > 0 && (
        <div className="py-4 border-b border-[#F0F0F0] last:border-0">
          <p className="text-xs font-medium text-[#0E1628] mb-3">
            Magnets ({magnets.length})
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {magnets.map((magnet, mi) => (
              <div
                key={mi}
                className="flex gap-3 bg-[#F9FAFB] p-3 border border-[#E8E8E8]"
              >
                <div className="w-16 h-16 flex-shrink-0 overflow-hidden bg-white">
                  {magnet.uploadedImages?.[0]?.url ? (
                    <img
                      src={magnet.uploadedImages[0].url}
                      alt={`Magnet ${mi + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                      No image
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-xs font-medium text-[#1C2A46] truncate">
                    {magnet.memoryTitle || `Magnet ${mi + 1}`}
                  </p>
                  <p className="text-[11px] text-[#6B635D] leading-snug line-clamp-3 mt-1">
                    {magnet.memoryStory || 'No story'}
                  </p>
                  {magnet.uploadedImages?.length > 1 && (
                    <p className="text-[10px] text-[#8A9099] mt-1">
                      +{magnet.uploadedImages.length - 1} more image(s)
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailsModal;